/**
 * AdminSidebar — fixed dark forest sidebar for the Admin Web Panel.
 * Mirrors the design reference: brand mark, sectioned menu, profile footer.
 */
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  UserCircle,
  Briefcase,
  LayoutGrid,
  ClipboardList,
  Calendar,
  MessageCircle,
  FolderOpen,
  BarChart3,
  CreditCard,
  Settings as SettingsIcon,
  Shield,
  Building2,
  History,
  Lock,
  LogOut,
  Sparkles,
  Inbox,
  Languages,
  ClipboardCheck,
  Activity,
  AlertOctagon,
  UtensilsCrossed,
  GraduationCap,
  HeartPulse,
  Handshake,
  FolderArchive,
  Plug,
  CalendarRange,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";

export default function AdminSidebar() {
  const { user, signOut } = useAuth();
  const { t } = useI18n();

  const SECTIONS = [
    {
      label: "",
      items: [
        { to: "/admin", icon: LayoutDashboard, label: t("admin_panel.sidebar.nav_overview"), end: true, testid: "admin-nav-overview" },
      ],
    },
    {
      label: t("admin_panel.sidebar.section_operations"),
      items: [
        { to: "/admin/children", icon: Users, label: t("admin_panel.sidebar.nav_children"), testid: "admin-nav-children" },
        { to: "/admin/parents", icon: UserCircle, label: t("admin_panel.sidebar.nav_parents"), testid: "admin-nav-parents" },
        { to: "/admin/staff", icon: Briefcase, label: t("admin_panel.sidebar.nav_staff"), testid: "admin-nav-staff" },
        { to: "/admin/groups", icon: LayoutGrid, label: t("admin_panel.sidebar.nav_groups"), testid: "admin-nav-groups" },
        { to: "/admin/smart-building", icon: Building2, label: t("admin_panel.sidebar.nav_smart_building"), testid: "admin-nav-smart-building" },
        { to: "/admin/attendance", icon: ClipboardList, label: t("admin_panel.sidebar.nav_attendance"), testid: "admin-nav-attendance" },
      ],
    },
    {
      label: t("admin_panel.sidebar.section_communication"),
      items: [
        { to: "/admin/calendar", icon: Calendar, label: t("admin_panel.sidebar.nav_calendar"), testid: "admin-nav-calendar" },
        { to: "/admin/calendar-sync", icon: CalendarRange, label: t("admin_panel.sidebar.nav_calendar_sync"), testid: "admin-nav-calendar-sync" },
        { to: "/admin/messages", icon: MessageCircle, label: t("admin_panel.sidebar.nav_messages"), testid: "admin-nav-messages" },
        { to: "/admin/demo-requests", icon: Inbox, label: t("admin_panel.sidebar.nav_demo_requests"), testid: "admin-nav-demo-requests" },
        { to: "/admin/admissions", icon: ClipboardCheck, label: t("admin_panel.sidebar.nav_admissions"), testid: "admin-nav-admissions" },
        { to: "/admin/documents", icon: FolderOpen, label: t("admin_panel.sidebar.nav_documents"), testid: "admin-nav-documents" },
        { to: "/admin/documents-hub", icon: FolderArchive, label: t("admin_panel.sidebar.nav_documents_hub"), testid: "admin-nav-documents-hub" },
        { to: "/admin/vendors", icon: Handshake, label: t("admin_panel.sidebar.nav_vendors"), testid: "admin-nav-vendors" },
      ],
    },
    {
      label: t("admin_panel.sidebar.section_operations"),
      items: [
        { to: "/admin/ops/ratio", icon: Activity, label: t("admin_panel.sidebar.nav_live_ratio"), testid: "admin-nav-ratio" },
        { to: "/admin/ops/daily-logs", icon: ClipboardList, label: t("admin_panel.sidebar.nav_daily_logs"), testid: "admin-nav-daily-logs" },
        { to: "/admin/ops/observations", icon: GraduationCap, label: t("admin_panel.sidebar.nav_observations"), testid: "admin-nav-observations" },
        { to: "/admin/ops/incidents", icon: AlertOctagon, label: t("admin_panel.sidebar.nav_incidents"), testid: "admin-nav-incidents" },
        { to: "/admin/ops/meal-plans", icon: UtensilsCrossed, label: t("admin_panel.sidebar.nav_meal_plans"), testid: "admin-nav-meal-plans" },
        { to: "/admin/ops/schedule", icon: Calendar, label: t("admin_panel.sidebar.nav_staff_schedule"), testid: "admin-nav-schedule" },
        { to: "/admin/ops/certifications", icon: HeartPulse, label: t("admin_panel.sidebar.nav_certifications"), testid: "admin-nav-certifications" },
      ],
    },
    {
      label: t("admin_panel.sidebar.section_money"),
      items: [
        { to: "/admin/billing", icon: CreditCard, label: t("admin_panel.sidebar.nav_billing"), testid: "admin-nav-billing" },
        { to: "/admin/billing/invoices", icon: CreditCard, label: t("admin_panel.sidebar.nav_invoices"), testid: "admin-nav-billing-invoices" },
        { to: "/admin/billing/fee-plans", icon: CreditCard, label: t("admin_panel.sidebar.nav_fee_plans"), testid: "admin-nav-billing-fee-plans" },
        { to: "/admin/billing/settings", icon: CreditCard, label: t("admin_panel.sidebar.nav_billing_settings"), testid: "admin-nav-billing-settings" },
        { to: "/admin/accounting", icon: Plug, label: t("admin_panel.sidebar.nav_accounting"), testid: "admin-nav-accounting" },
      ],
    },
    {
      label: t("admin_panel.sidebar.section_insight"),
      items: [
        { to: "/admin/reports", icon: BarChart3, label: t("admin_panel.sidebar.nav_reports"), testid: "admin-nav-reports" },
        { to: "/admin/status-history", icon: Sparkles, label: t("admin_panel.sidebar.nav_status_history"), testid: "admin-nav-status-history" },
      ],
    },
    {
      label: t("admin_panel.sidebar.section_governance"),
      items: [
        { to: "/admin/permissions", icon: Shield, label: t("admin_panel.sidebar.nav_permissions"), testid: "admin-nav-permissions" },
        { to: "/admin/audit-logs", icon: History, label: t("admin_panel.sidebar.nav_audit_logs"), testid: "admin-nav-audit-logs" },
        { to: "/admin/pickup-log", icon: Users, label: t("admin_panel.sidebar.nav_pickup_log"), testid: "admin-nav-pickup-log" },
        { to: "/admin/privacy", icon: Lock, label: t("admin_panel.sidebar.nav_privacy"), testid: "admin-nav-privacy" },
        { to: "/admin/translation-feedback", icon: Languages, label: t("admin_panel.sidebar.nav_translation_feedback"), testid: "admin-nav-translation-feedback" },
        { to: "/admin/i18n-coverage", icon: Languages, label: t("admin_panel.sidebar.nav_i18n_coverage"), testid: "admin-nav-i18n-coverage" },
        { to: "/admin/settings", icon: SettingsIcon, label: t("admin_panel.sidebar.nav_settings"), testid: "admin-nav-settings" },
      ],
    },
  ];

  const item = ({ isActive }) =>
    `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm transition-colors relative ${
      isActive
        ? "bg-[#1F3A2A]/15 text-white font-semibold before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-full before:bg-[#1F3A2A]"
        : "text-white/70 hover:bg-white/8 hover:text-white"
    }`;
  return (
    <aside
      data-testid="admin-sidebar"
      className="hidden lg:flex w-64 shrink-0 flex-col bg-[#132619] text-white sticky top-0 h-screen"
    >
      <div className="px-6 py-7 bg-[#FAF9F1] -mx-0 mb-2">
        <img
          src="/brand/alouvya-wordmark-tight.svg"
          alt="Alouvya"
          className="h-10 w-auto object-contain"
        />
        <div className="mt-2 text-[10px] tracking-[0.18em] uppercase text-[#6B7280] font-semibold">
          {t("admin_panel.sidebar.director_panel")}
        </div>
      </div>
      <nav className="px-3 pb-6 flex-1 overflow-y-auto">
        {SECTIONS.map((s, idx) => (
          <div key={idx} className={s.label ? "mt-6" : ""}>
            {s.label && (
              <div className="px-4 mb-2 text-[10px] tracking-[0.18em] uppercase text-white/40 font-semibold">
                {s.label}
              </div>
            )}
            <ul className="space-y-0.5">
              {s.items.map((it) => (
                <li key={it.to}>
                  <NavLink
                    to={it.to}
                    end={it.end}
                    data-testid={it.testid}
                    className={item}
                  >
                    <it.icon size={18} strokeWidth={1.6} />
                    <span>{it.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <div className="px-6 pb-7 border-t border-white/10 pt-5">
        <div className="text-sm text-white font-semibold admin-sidebar-name" data-testid="admin-sidebar-user-name">
          {user?.name || "—"}
        </div>
        <div className="text-[10px] tracking-[0.18em] uppercase text-white/50 mt-1 font-medium">
          {user?.role || "—"}
        </div>
        <button
          type="button"
          onClick={signOut}
          data-testid="admin-sidebar-signout"
          className="mt-4 inline-flex items-center gap-2 text-xs text-white/70 hover:text-white"
        >
          <LogOut size={12} strokeWidth={1.6} />
          {t("admin_panel.sidebar.sign_out")}
        </button>
      </div>
    </aside>
  );
}
